import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReportRange } from '@order-pilot/shared';

/**
 * Report filters live in the URL query string
 * (`?preset=&from=&to=&branchId=&deviceId=`)
 * so every report view is shareable, bookmarkable and survives a reload; the
 * query keys are derived from it, so navigating a filter refetches.
 *
 * All dates are POS-local "YYYY-MM-DD" strings. Arithmetic goes through UTC
 * parts purely as a calendar (constructed and read with getUTC*), which never
 * shifts a day the way a local-time round-trip can.
 */
export type RangePreset = 'today' | 'yesterday' | 'last7' | 'thisMonth' | 'lastMonth' | 'custom';

export const RANGE_PRESETS: ReadonlyArray<{ id: RangePreset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'custom', label: 'Custom' },
];

// Today, like the till's own report pages: an owner opening the portal is
// almost always checking the day in progress. Picking another period still
// carries across pages until changed.
const DEFAULT_PRESET: RangePreset = 'today';
const ISO_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isPosDate(value: string | null | undefined): value is string {
  return typeof value === 'string' && ISO_DATE.test(value);
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function toCalendar(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function fromCalendar(date: Date): string {
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function addDays(date: string, days: number): string {
  const calendar = toCalendar(date);
  calendar.setUTCDate(calendar.getUTCDate() + days);
  return fromCalendar(calendar);
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: string): string {
  const calendar = toCalendar(startOfMonth(date));
  calendar.setUTCMonth(calendar.getUTCMonth() + 1);
  calendar.setUTCDate(0);
  return fromCalendar(calendar);
}

export function addMonths(date: string, months: number): string {
  const calendar = toCalendar(startOfMonth(date));
  calendar.setUTCMonth(calendar.getUTCMonth() + months);
  return fromCalendar(calendar);
}

/**
 * "Today" as the tenant's own calendar sees it — the POS writes its local
 * dates, so a browser in another timezone must not shift the range.
 */
export function todayIn(timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  const now = new Date();
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', { ...options, timeZone: timezone }).formatToParts(now);
  } catch {
    // An unknown IANA zone must not break the portal; fall back to the browser's.
    parts = new Intl.DateTimeFormat('en-US', options).formatToParts(now);
  }
  const find = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  const year = find('year');
  const month = find('month');
  const day = find('day');
  return year && month && day ? `${year}-${month}-${day}` : fromCalendar(now);
}

export interface DateRange {
  from: string;
  to: string;
}

/** Resolves a preset against the tenant's today. `custom` keeps the given range. */
export function resolvePreset(preset: RangePreset, today: string, current?: DateRange): DateRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const day = addDays(today, -1);
      return { from: day, to: day };
    }
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'thisMonth':
      return { from: startOfMonth(today), to: today };
    case 'lastMonth': {
      const previous = addMonths(today, -1);
      return { from: startOfMonth(previous), to: endOfMonth(previous) };
    }
    case 'custom':
      // Only the dates: callers spread this over their filters, and passing
      // `current` itself would also carry its old preset back in.
      return current ? { from: current.from, to: current.to } : { from: addDays(today, -6), to: today };
  }
}

export interface ReportFilters extends DateRange {
  preset: RangePreset;
  /** null = all branches aggregated. */
  branchId: string | null;
  /** null = every till aggregated. */
  deviceId: string | null;
}

/** The DTO-shaped slice a report request needs. */
export function toReportRange(filters: ReportFilters): ReportRange {
  return {
    from: filters.from,
    to: filters.to,
    ...(filters.branchId === null ? {} : { branchId: filters.branchId }),
    ...(filters.deviceId === null ? {} : { deviceId: filters.deviceId }),
  };
}

function isPreset(value: string | null): value is RangePreset {
  return value !== null && RANGE_PRESETS.some((preset) => preset.id === value);
}

function buildSearch(filters: ReportFilters, extra?: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(extra);
  next.set('preset', filters.preset);
  next.set('from', filters.from);
  next.set('to', filters.to);
  if (filters.branchId === null) next.delete('branchId');
  else next.set('branchId', filters.branchId);
  if (filters.deviceId === null) next.delete('deviceId');
  else next.set('deviceId', filters.deviceId);
  return next;
}

export interface ReportFiltersState {
  filters: ReportFilters;
  setPreset: (preset: RangePreset) => void;
  setRange: (range: Partial<DateRange>) => void;
  setBranch: (branchId: string | null) => void;
  setDevice: (deviceId: string | null) => void;
  /**
   * Query string for nav links: the branch / till filters, but NOT the period
   * — every report opens on Today, whatever the previous page showed.
   */
  search: string;
  today: string;
}

/**
 * Reads the filters out of the URL, filling in and normalising anything missing
 * so the address bar always shows the full, shareable state. A named preset is
 * re-resolved on every read, so a bookmarked `?preset=today` still means today
 * tomorrow.
 */
export function useReportFilters(timezone: string | undefined): ReportFiltersState {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIn(timezone);

  const rawPreset = searchParams.get('preset');
  const rawFrom = searchParams.get('from');
  const rawTo = searchParams.get('to');
  const rawBranch = searchParams.get('branchId');
  const rawDevice = searchParams.get('deviceId');

  const filters = useMemo<ReportFilters>(() => {
    const hasExplicitRange = isPosDate(rawFrom) && isPosDate(rawTo);
    const preset: RangePreset = isPreset(rawPreset) ? rawPreset : hasExplicitRange ? 'custom' : DEFAULT_PRESET;
    const requested = hasExplicitRange ? { from: rawFrom, to: rawTo } : undefined;
    const resolved = resolvePreset(preset, today, requested);
    // A backwards custom range is a typo, not an error state — swap it.
    const range = resolved.from > resolved.to ? { from: resolved.to, to: resolved.from } : resolved;
    return {
      ...range,
      preset,
      branchId: rawBranch !== null && rawBranch !== '' ? rawBranch : null,
      deviceId: rawDevice !== null && rawDevice !== '' ? rawDevice : null,
    };
  }, [rawPreset, rawFrom, rawTo, rawBranch, rawDevice, today]);

  // Write the canonical state back once, so reloads and shared links are stable.
  useEffect(() => {
    const canonical = buildSearch(filters, searchParams);
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const apply = useCallback(
    (next: ReportFilters) => {
      setSearchParams(buildSearch(next, searchParams));
    },
    [searchParams, setSearchParams],
  );

  const setPreset = useCallback(
    (preset: RangePreset) => {
      apply({ ...filters, preset, ...resolvePreset(preset, today, filters) });
    },
    [apply, filters, today],
  );

  const setRange = useCallback(
    (range: Partial<DateRange>) => {
      const from = isPosDate(range.from) ? range.from : filters.from;
      const to = isPosDate(range.to) ? range.to : filters.to;
      apply({ ...filters, preset: 'custom', from, to });
    },
    [apply, filters],
  );

  const setBranch = useCallback(
    (branchId: string | null) => {
      // Narrowing to a branch drops any till selection: the chosen till may not
      // belong to the new branch, and the API would 404 on it.
      apply({ ...filters, branchId, deviceId: null });
    },
    [apply, filters],
  );

  const setDevice = useCallback(
    (deviceId: string | null) => {
      apply({ ...filters, deviceId });
    },
    [apply, filters],
  );

  // The period stays with the page it was picked on (its URL, so a reload
  // keeps it); moving to another report starts again at Today.
  const search = useMemo(() => {
    const next = new URLSearchParams();
    if (filters.branchId) next.set('branchId', filters.branchId);
    if (filters.deviceId) next.set('deviceId', filters.deviceId);
    const query = next.toString();
    return query ? `?${query}` : '';
  }, [filters.branchId, filters.deviceId]);

  // Stable identity, so consumers of the portal context are not re-rendered by
  // a fresh object on every parent render.
  return useMemo(
    () => ({ filters, setPreset, setRange, setBranch, setDevice, search, today }),
    [filters, setPreset, setRange, setBranch, setDevice, search, today],
  );
}
